/*
  Named palette registry — the single source of truth for every theme variant.

  Two independent axes:
    1. PALETTES   — the full theme (surfaces, text, brand primary, hero, and a
                    NATIVE action/accent color). Each has light + dark modes.
    2. BUTTON_PALETTES — optional action-color overlays. Selecting one overrides
                    only the CTA tokens (accent + whatsapp) on top of whatever
                    theme is active, so you can mix muted/pastel buttons with any
                    theme's primaries. Empty selection = use the theme's native
                    accent.

  Palette VALUES live here; lib/theme.ts writes them as CSS custom properties on
  <html>, which Tailwind reads via `@theme inline`. The ThemeSwitcher renders its
  controls from these lists.

  Iterating safely: to try a new look without losing the old one, COPY an entry
  to a new `id` (e.g. "shiraz-v2") rather than editing in place — both then show
  up in the switcher side by side.
*/

export type Mode = "light" | "dark"

export type ThemeTokens = {
  background: string
  foreground: string
  card: string
  cardForeground: string
  primary: string
  primaryForeground: string
  secondary: string
  secondaryForeground: string
  muted: string
  mutedForeground: string
  accent: string
  accentForeground: string
  whatsapp: string
  whatsappForeground: string
  border: string
  input: string
  ring: string
  radius: string
  heroFrom: string
  heroVia: string
  heroTo: string
}

export type Palette = {
  id: string
  label: string
  description: string
  modes: Partial<Record<Mode, ThemeTokens>>
}

export const PALETTES: Palette[] = [
  {
    id: "dusk",
    label: "Dusk",
    description: "Lavender sky → purple haze, coral CTA. Closest to the original hero.",
    modes: {
      light: {
        background: "#faf8fc",
        foreground: "#2e2838",
        card: "#ffffff",
        cardForeground: "#2e2838",
        primary: "#6b4f9e",
        primaryForeground: "#ffffff",
        secondary: "#ece6f5",
        secondaryForeground: "#3f3552",
        muted: "#f1edf7",
        mutedForeground: "#837a93",
        accent: "#e8533f",
        accentForeground: "#ffffff",
        whatsapp: "#25d366",
        whatsappForeground: "#ffffff",
        border: "#e6def0",
        input: "#e6def0",
        ring: "#6b4f9e",
        radius: "0.9rem",
        heroFrom: "#cabfe6",
        heroVia: "#6b4f9e",
        heroTo: "#2a2138",
      },
      dark: {
        background: "#1c1726",
        foreground: "#ece7f4",
        card: "#271f35",
        cardForeground: "#ece7f4",
        primary: "#b79ce0",
        primaryForeground: "#1c1726",
        secondary: "#342a47",
        secondaryForeground: "#ddd4ec",
        muted: "#2d2540",
        mutedForeground: "#a99fc0",
        accent: "#f06750",
        accentForeground: "#1c1726",
        whatsapp: "#25d366",
        whatsappForeground: "#0c1f12",
        border: "#392f4d",
        input: "#392f4d",
        ring: "#b79ce0",
        radius: "0.9rem",
        heroFrom: "#4a3d68",
        heroVia: "#2f2742",
        heroTo: "#120f1a",
      },
    },
  },
  {
    id: "twilight",
    label: "Twilight",
    description: "Periwinkle & slate-blue from the cooler sky and distant ridges. Coral CTA pops.",
    modes: {
      light: {
        background: "#f7f8fc",
        foreground: "#232838",
        card: "#ffffff",
        cardForeground: "#232838",
        primary: "#3f4ea0",
        primaryForeground: "#ffffff",
        secondary: "#e7eaf6",
        secondaryForeground: "#353f5e",
        muted: "#eef1f8",
        mutedForeground: "#767f99",
        accent: "#e8533f",
        accentForeground: "#ffffff",
        whatsapp: "#1ebe5d",
        whatsappForeground: "#ffffff",
        border: "#e0e4f0",
        input: "#e0e4f0",
        ring: "#3f4ea0",
        radius: "0.9rem",
        heroFrom: "#bcc3e6",
        heroVia: "#4f5a96",
        heroTo: "#1f2336",
      },
      dark: {
        background: "#161a2b",
        foreground: "#e7eaf6",
        card: "#20263d",
        cardForeground: "#e7eaf6",
        primary: "#97a6e6",
        primaryForeground: "#161a2b",
        secondary: "#2b3350",
        secondaryForeground: "#d6dcf0",
        muted: "#242b44",
        mutedForeground: "#9aa3c2",
        accent: "#f06750",
        accentForeground: "#161a2b",
        whatsapp: "#25d366",
        whatsappForeground: "#0a1f12",
        border: "#323a59",
        input: "#323a59",
        ring: "#97a6e6",
        radius: "0.9rem",
        heroFrom: "#3b4574",
        heroVia: "#232a45",
        heroTo: "#11131f",
      },
    },
  },
  {
    id: "winelands",
    label: "Winelands",
    description: "Cape Winelands — vineyard green, golden light and terracotta earth.",
    modes: {
      light: {
        background: "#faf8f2",
        foreground: "#2c2a1f",
        card: "#ffffff",
        cardForeground: "#2c2a1f",
        primary: "#4f6440",
        primaryForeground: "#ffffff",
        secondary: "#ece7d8",
        secondaryForeground: "#4a4634",
        muted: "#f1ede1",
        mutedForeground: "#837d68",
        accent: "#b5572e",
        accentForeground: "#ffffff",
        whatsapp: "#3f9e63",
        whatsappForeground: "#ffffff",
        border: "#e6e0cf",
        input: "#e6e0cf",
        ring: "#4f6440",
        radius: "0.85rem",
        heroFrom: "#d9c49a",
        heroVia: "#7a8a52",
        heroTo: "#3a3322",
      },
      dark: {
        background: "#1f2118",
        foreground: "#ece8da",
        card: "#2a2c20",
        cardForeground: "#ece8da",
        primary: "#9bb07e",
        primaryForeground: "#1f2118",
        secondary: "#34372a",
        secondaryForeground: "#ddd9c8",
        muted: "#2d3022",
        mutedForeground: "#a8a48f",
        accent: "#d77a4f",
        accentForeground: "#1f2118",
        whatsapp: "#25d366",
        whatsappForeground: "#0a1f12",
        border: "#3a3d2e",
        input: "#3a3d2e",
        ring: "#9bb07e",
        radius: "0.85rem",
        heroFrom: "#4a4a2e",
        heroVia: "#2f3320",
        heroTo: "#14150e",
      },
    },
  },
  {
    id: "shiraz",
    label: "Shiraz",
    description: "Deep red wine — burgundy & oxblood with a champagne-gold CTA. Elegant.",
    modes: {
      light: {
        background: "#fbf6f4",
        foreground: "#2e1a1d",
        card: "#ffffff",
        cardForeground: "#2e1a1d",
        primary: "#6e1f2a",
        primaryForeground: "#fbf0ec",
        secondary: "#f0dfdc",
        secondaryForeground: "#4f2a2e",
        muted: "#f5e9e6",
        mutedForeground: "#8a6a6c",
        accent: "#c0913a",
        accentForeground: "#2e1810",
        whatsapp: "#3f9e63",
        whatsappForeground: "#ffffff",
        border: "#ecdad6",
        input: "#ecdad6",
        ring: "#6e1f2a",
        radius: "0.7rem",
        heroFrom: "#b5697a",
        heroVia: "#6e1f2a",
        heroTo: "#2c0f15",
      },
      dark: {
        background: "#1d1013",
        foreground: "#f3e4e2",
        card: "#2a181c",
        cardForeground: "#f3e4e2",
        primary: "#c75c6e",
        primaryForeground: "#1d1013",
        secondary: "#3a2126",
        secondaryForeground: "#ecd6d3",
        muted: "#321a1f",
        mutedForeground: "#b08c8f",
        accent: "#d9a94a",
        accentForeground: "#1d1013",
        whatsapp: "#25d366",
        whatsappForeground: "#0a1f12",
        border: "#41252a",
        input: "#41252a",
        ring: "#c75c6e",
        radius: "0.7rem",
        heroFrom: "#5e2630",
        heroVia: "#371519",
        heroTo: "#160a0c",
      },
    },
  },
  {
    id: "summer-sunset",
    label: "Summer Sunset",
    description: "Vivid Cape summer dusk — gold to orange to magenta. Warm and saturated.",
    modes: {
      light: {
        background: "#fff7f0",
        foreground: "#36231f",
        card: "#ffffff",
        cardForeground: "#36231f",
        primary: "#d4452b",
        primaryForeground: "#ffffff",
        secondary: "#ffe2d0",
        secondaryForeground: "#5a3326",
        muted: "#fff0e6",
        mutedForeground: "#93705f",
        accent: "#c0398a",
        accentForeground: "#ffffff",
        whatsapp: "#2bb673",
        whatsappForeground: "#ffffff",
        border: "#ffded0",
        input: "#ffded0",
        ring: "#d4452b",
        radius: "0.9rem",
        heroFrom: "#ffb14a",
        heroVia: "#f0653a",
        heroTo: "#a02765",
      },
      dark: {
        background: "#241413",
        foreground: "#ffe8dd",
        card: "#321b18",
        cardForeground: "#ffe8dd",
        primary: "#f47a52",
        primaryForeground: "#241413",
        secondary: "#432420",
        secondaryForeground: "#ffd6c6",
        muted: "#3a1e1a",
        mutedForeground: "#c39a8c",
        accent: "#e06aa0",
        accentForeground: "#241413",
        whatsapp: "#25d366",
        whatsappForeground: "#0a1f12",
        border: "#4a2722",
        input: "#4a2722",
        ring: "#f47a52",
        radius: "0.9rem",
        heroFrom: "#9c4a2e",
        heroVia: "#5a234a",
        heroTo: "#1a0f14",
      },
    },
  },
  {
    id: "winter-sunset",
    label: "Winter Sunset",
    description: "Cape winter dusk — muted mauve, cold rose and slate. Soft and quiet.",
    modes: {
      light: {
        background: "#f8f6f8",
        foreground: "#2c2a32",
        card: "#ffffff",
        cardForeground: "#2c2a32",
        primary: "#7a6b86",
        primaryForeground: "#ffffff",
        secondary: "#e8e3ea",
        secondaryForeground: "#463f4e",
        muted: "#efebf0",
        mutedForeground: "#837d8a",
        accent: "#c0697a",
        accentForeground: "#ffffff",
        whatsapp: "#4f9e7a",
        whatsappForeground: "#ffffff",
        border: "#e4dee6",
        input: "#e4dee6",
        ring: "#7a6b86",
        radius: "0.85rem",
        heroFrom: "#cdbfce",
        heroVia: "#8a7794",
        heroTo: "#3e3a48",
      },
      dark: {
        background: "#1e1c24",
        foreground: "#e9e5ec",
        card: "#29262f",
        cardForeground: "#e9e5ec",
        primary: "#b3a3bf",
        primaryForeground: "#1e1c24",
        secondary: "#342f3c",
        secondaryForeground: "#ddd5e2",
        muted: "#2c2833",
        mutedForeground: "#a59eb0",
        accent: "#d68a98",
        accentForeground: "#1e1c24",
        whatsapp: "#25d366",
        whatsappForeground: "#0a1f12",
        border: "#3a3543",
        input: "#3a3543",
        ring: "#b3a3bf",
        radius: "0.85rem",
        heroFrom: "#4a4255",
        heroVia: "#2e2a36",
        heroTo: "#14121a",
      },
    },
  },
  {
    id: "summer-sunrise",
    label: "Summer Sunrise",
    description: "Bright summer dawn — coral and warm gold meeting a fresh morning-sky blue.",
    modes: {
      light: {
        background: "#fff8f3",
        foreground: "#2b2a2e",
        card: "#ffffff",
        cardForeground: "#2b2a2e",
        primary: "#e2705a",
        primaryForeground: "#ffffff",
        secondary: "#ffe6d6",
        secondaryForeground: "#5a392c",
        muted: "#fff1e8",
        mutedForeground: "#8f7468",
        accent: "#f3a82e",
        accentForeground: "#2b2118",
        whatsapp: "#2bb673",
        whatsappForeground: "#ffffff",
        border: "#ffe0d2",
        input: "#ffe0d2",
        ring: "#e2705a",
        radius: "0.95rem",
        heroFrom: "#ffd27a",
        heroVia: "#f58a6a",
        heroTo: "#6fa8c0",
      },
      dark: {
        background: "#221619",
        foreground: "#ffe9e0",
        card: "#2f1d20",
        cardForeground: "#ffe9e0",
        primary: "#f58a6f",
        primaryForeground: "#221619",
        secondary: "#402420",
        secondaryForeground: "#ffd6c8",
        muted: "#371f1d",
        mutedForeground: "#c39a8e",
        accent: "#f3bf5a",
        accentForeground: "#221619",
        whatsapp: "#25d366",
        whatsappForeground: "#0a1f12",
        border: "#482824",
        input: "#482824",
        ring: "#f58a6f",
        radius: "0.95rem",
        heroFrom: "#9c5a4a",
        heroVia: "#4a3a52",
        heroTo: "#161018",
      },
    },
  },
  {
    id: "winter-sunrise",
    label: "Winter Sunrise",
    description: "Crisp cold dawn — soft peach over cool grey-blue. Quiet and fresh.",
    modes: {
      light: {
        background: "#f7f8fa",
        foreground: "#2a2e34",
        card: "#ffffff",
        cardForeground: "#2a2e34",
        primary: "#5f7488",
        primaryForeground: "#ffffff",
        secondary: "#e7eaef",
        secondaryForeground: "#3e4854",
        muted: "#eef0f3",
        mutedForeground: "#79828f",
        accent: "#e0a07a",
        accentForeground: "#3a2418",
        whatsapp: "#4f9e7a",
        whatsappForeground: "#ffffff",
        border: "#e2e5ea",
        input: "#e2e5ea",
        ring: "#5f7488",
        radius: "0.8rem",
        heroFrom: "#f5d8bf",
        heroVia: "#c2b6b0",
        heroTo: "#5f6e80",
      },
      dark: {
        background: "#181b21",
        foreground: "#e6eaf0",
        card: "#222630",
        cardForeground: "#e6eaf0",
        primary: "#94a6ba",
        primaryForeground: "#181b21",
        secondary: "#2c313b",
        secondaryForeground: "#d4dae3",
        muted: "#252a33",
        mutedForeground: "#98a1ae",
        accent: "#e0a884",
        accentForeground: "#181b21",
        whatsapp: "#25d366",
        whatsappForeground: "#0a1f12",
        border: "#313742",
        input: "#313742",
        ring: "#94a6ba",
        radius: "0.8rem",
        heroFrom: "#46505f",
        heroVia: "#2a2f39",
        heroTo: "#14171c",
      },
    },
  },
  {
    id: "savanna",
    label: "Savanna",
    description: "Bushveld — ochre and dry-grass gold, acacia green and red-earth CTA.",
    modes: {
      light: {
        background: "#fdfaf3",
        foreground: "#2f2a1e",
        card: "#ffffff",
        cardForeground: "#2f2a1e",
        primary: "#b9712a",
        primaryForeground: "#ffffff",
        secondary: "#efe6d2",
        secondaryForeground: "#574d34",
        muted: "#f4eddc",
        mutedForeground: "#8a7d60",
        accent: "#c0392b",
        accentForeground: "#ffffff",
        whatsapp: "#4f9e5a",
        whatsappForeground: "#ffffff",
        border: "#e9dfc8",
        input: "#e9dfc8",
        ring: "#b9712a",
        radius: "0.85rem",
        heroFrom: "#e8c887",
        heroVia: "#b9712a",
        heroTo: "#5a3a1e",
      },
      dark: {
        background: "#211c14",
        foreground: "#ece5d4",
        card: "#2c2619",
        cardForeground: "#ece5d4",
        primary: "#d99a52",
        primaryForeground: "#211c14",
        secondary: "#382f1f",
        secondaryForeground: "#ddd3bd",
        muted: "#2f2818",
        mutedForeground: "#aa9d7e",
        accent: "#d9533f",
        accentForeground: "#211c14",
        whatsapp: "#25d366",
        whatsappForeground: "#0a1f12",
        border: "#3d3422",
        input: "#3d3422",
        ring: "#d99a52",
        radius: "0.85rem",
        heroFrom: "#5a4424",
        heroVia: "#33291a",
        heroTo: "#16120c",
      },
    },
  },
  {
    id: "veld",
    label: "Veld",
    description: "Dry-grass khaki and olive vineyard greens with a warm dry-grass gold CTA.",
    modes: {
      light: {
        background: "#f9f8f1",
        foreground: "#2b2c20",
        card: "#ffffff",
        cardForeground: "#2b2c20",
        primary: "#6b7444",
        primaryForeground: "#ffffff",
        secondary: "#e9e8d4",
        secondaryForeground: "#4a4c34",
        muted: "#f0efe0",
        mutedForeground: "#807f63",
        accent: "#c08a3a",
        accentForeground: "#2e2410",
        whatsapp: "#4f9e5a",
        whatsappForeground: "#ffffff",
        border: "#e4e2cd",
        input: "#e4e2cd",
        ring: "#6b7444",
        radius: "0.85rem",
        heroFrom: "#cfc99a",
        heroVia: "#7c804a",
        heroTo: "#38381f",
      },
      dark: {
        background: "#1f2016",
        foreground: "#e9e8d6",
        card: "#292a1d",
        cardForeground: "#e9e8d6",
        primary: "#a7b07a",
        primaryForeground: "#1f2016",
        secondary: "#343623",
        secondaryForeground: "#d8d7c0",
        muted: "#2c2d1e",
        mutedForeground: "#a3a283",
        accent: "#d6a44e",
        accentForeground: "#1f2016",
        whatsapp: "#25d366",
        whatsappForeground: "#0a1f12",
        border: "#3a3b27",
        input: "#3a3b27",
        ring: "#a7b07a",
        radius: "0.85rem",
        heroFrom: "#494a2b",
        heroVia: "#2e2f1d",
        heroTo: "#14140d",
      },
    },
  },
  {
    id: "protea",
    label: "Protea",
    description: "Fynbos — protea pink-magenta with foliage green. Bold and floral.",
    modes: {
      light: {
        background: "#fbf6f8",
        foreground: "#2c2630",
        card: "#ffffff",
        cardForeground: "#2c2630",
        primary: "#b03a6a",
        primaryForeground: "#ffffff",
        secondary: "#f0dde6",
        secondaryForeground: "#5a3346",
        muted: "#f5e8ee",
        mutedForeground: "#8c7480",
        accent: "#4f8a5e",
        accentForeground: "#ffffff",
        whatsapp: "#3f9e63",
        whatsappForeground: "#ffffff",
        border: "#ecd9e2",
        input: "#ecd9e2",
        ring: "#b03a6a",
        radius: "0.9rem",
        heroFrom: "#e3a7c0",
        heroVia: "#b03a6a",
        heroTo: "#46343f",
      },
      dark: {
        background: "#221620",
        foreground: "#f1e3ea",
        card: "#2f1d29",
        cardForeground: "#f1e3ea",
        primary: "#d96e98",
        primaryForeground: "#221620",
        secondary: "#3c2433",
        secondaryForeground: "#ecd3df",
        muted: "#341f2c",
        mutedForeground: "#b793a4",
        accent: "#6fb07e",
        accentForeground: "#221620",
        whatsapp: "#25d366",
        whatsappForeground: "#0a1f12",
        border: "#45283a",
        input: "#45283a",
        ring: "#d96e98",
        radius: "0.9rem",
        heroFrom: "#5e2e48",
        heroVia: "#371a2a",
        heroTo: "#181015",
      },
    },
  },
  {
    id: "clean",
    label: "Clean",
    description: "Neutral white/grey with a purple accent. Max readability for content/SEO pages.",
    modes: {
      light: {
        background: "#ffffff",
        foreground: "#1f2430",
        card: "#ffffff",
        cardForeground: "#1f2430",
        primary: "#5b3fa0",
        primaryForeground: "#ffffff",
        secondary: "#f3f4f6",
        secondaryForeground: "#374151",
        muted: "#f6f7f9",
        mutedForeground: "#6b7280",
        accent: "#e8533f",
        accentForeground: "#ffffff",
        whatsapp: "#1ebe5d",
        whatsappForeground: "#ffffff",
        border: "#e5e7eb",
        input: "#e5e7eb",
        ring: "#5b3fa0",
        radius: "0.75rem",
        heroFrom: "#ede8f7",
        heroVia: "#b9a7df",
        heroTo: "#5b3fa0",
      },
      dark: {
        background: "#16181d",
        foreground: "#e7e9ee",
        card: "#1f2228",
        cardForeground: "#e7e9ee",
        primary: "#a78be0",
        primaryForeground: "#16181d",
        secondary: "#2a2d35",
        secondaryForeground: "#d4d7df",
        muted: "#23262d",
        mutedForeground: "#9aa0ac",
        accent: "#f06750",
        accentForeground: "#16181d",
        whatsapp: "#25d366",
        whatsappForeground: "#0a1f12",
        border: "#30343d",
        input: "#30343d",
        ring: "#a78be0",
        radius: "0.75rem",
        heroFrom: "#33343d",
        heroVia: "#23242a",
        heroTo: "#141519",
      },
    },
  },
]

// ---- Action / button overlays (the second axis) ----
// Override only the CTA tokens, layered on top of any theme. Designed as muted /
// pastel mid-tones with dark foregrounds so they read softly on light AND dark
// cards. Empty selection ("") keeps the active theme's native accent.

export type ButtonTokens = {
  accent: string
  accentForeground: string
  whatsapp: string
  whatsappForeground: string
}

export type ButtonPalette = {
  id: string
  label: string
  description: string
  tokens: ButtonTokens
}

export const BUTTON_PALETTES: ButtonPalette[] = [
  {
    id: "coral-soft",
    label: "Soft Coral",
    description: "The default coral, muted — same warmth, less shout.",
    tokens: {
      accent: "#e0826f",
      accentForeground: "#3a1f17",
      whatsapp: "#7fc59a",
      whatsappForeground: "#163021",
    },
  },
  {
    id: "dusty-rose",
    label: "Dusty Rose",
    description: "Pastel rose — gentle, romantic, low-key.",
    tokens: {
      accent: "#d99aa0",
      accentForeground: "#4a2429",
      whatsapp: "#9cc7a8",
      whatsappForeground: "#234031",
    },
  },
  {
    id: "terracotta",
    label: "Terracotta",
    description: "Earthy clay — grounded and African.",
    tokens: {
      accent: "#c9784f",
      accentForeground: "#321608",
      whatsapp: "#6fae7a",
      whatsappForeground: "#14301d",
    },
  },
  {
    id: "amber",
    label: "Amber",
    description: "Warm town-lights gold — friendly and inviting.",
    tokens: {
      accent: "#e3b15f",
      accentForeground: "#3a2a12",
      whatsapp: "#7fbf8a",
      whatsappForeground: "#163021",
    },
  },
  {
    id: "sage",
    label: "Sage",
    description: "Muted green — calm and natural.",
    tokens: {
      accent: "#9cb592",
      accentForeground: "#28361f",
      whatsapp: "#88bf95",
      whatsappForeground: "#16301f",
    },
  },
  {
    id: "plum",
    label: "Plum",
    description: "Soft muted purple — understated and elegant.",
    tokens: {
      accent: "#a87fb0",
      accentForeground: "#2e1a33",
      whatsapp: "#88c19a",
      whatsappForeground: "#16301f",
    },
  },
  {
    id: "ocean",
    label: "Ocean",
    description: "Dusty teal — cool, coastal, calm.",
    tokens: {
      accent: "#7fb0b8",
      accentForeground: "#133031",
      whatsapp: "#7fc0a0",
      whatsappForeground: "#133023",
    },
  },
  {
    id: "sand",
    label: "Sand",
    description: "Warm neutral — quiet pastel for a stripped-back look.",
    tokens: {
      accent: "#d8c39c",
      accentForeground: "#463824",
      whatsapp: "#b7c79c",
      whatsappForeground: "#2c3320",
    },
  },
]

export const DEFAULT_PALETTE_ID = "dusk"
export const DEFAULT_MODE: Mode = "light"

export function getPalette(id: string): Palette {
  return PALETTES.find((p) => p.id === id) ?? PALETTES[0]
}

export function getButtonPalette(id: string | null | undefined): ButtonPalette | undefined {
  if (!id) return undefined
  return BUTTON_PALETTES.find((b) => b.id === id)
}
